import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { initStoreState } from '../actions';
import { Country } from '../../constants/country-phone-number';

export type PersonaInformation = {
	firstName: string;
	lastName: string;
	country: Country | '';
	dateOfBirth: string;
};

type State = {
	firstName: string;
	lastName: string;
	country: Country | '';
	dateOfBirth: string;
	email: string;
	phoneNumber: string;
	consentCollectingData: boolean;
	consentForwardingData: boolean;
	consentAdditional1: boolean;
	consentAdditional2: boolean;
	userAssessment: {
		user: Array<{
			code: string;
			value: string;
			comment: string;
		}>;
	};
};

const initialState: State = {
	firstName: '',
	lastName: '',
	country: '',
	dateOfBirth: '',
	email: '',
	phoneNumber: '',
	consentCollectingData: false,
	consentForwardingData: false,
	consentAdditional1: false,
	consentAdditional2: false,
	userAssessment: {
		user: []
	}
};

const personaSlice = createSlice({
	name: 'persona',
	initialState,
	reducers: {
		updatePersonaInformation(state, action: PayloadAction<PersonaInformation>) {
			const { firstName, lastName, country, dateOfBirth } = action.payload;
			state.firstName = firstName;
			state.lastName = lastName;
			state.country = country;
			state.dateOfBirth = dateOfBirth;
		},
		resetPersonaInformation(state) {
			state.firstName = '';
			state.lastName = '';
			state.country = '';
			state.dateOfBirth = '';
		},
		setPersonaInfo(state, action) {
			return {
				...state,
				...action.payload
			};
		},
		setPhoneNumber(state, action: PayloadAction<string>) {
			state.phoneNumber = action.payload;
		},
		setPersonaEmail(state, action) {
			state.email = action.payload;
		},
		setPersonaCollectingDataConsent(state, action) {
			state.consentCollectingData = action.payload;
		},
		setPersonaForwardingDataConsent(state, action) {
			state.consentForwardingData = action.payload;
		},
		setUserAssessmentArray(state, action) {
			if (!state.userAssessment?.user) {
				state.userAssessment = {
					user: []
				};
			}
			let item = action.payload;
			let index = state.userAssessment.user.findIndex((it) => it.code === item.code);
			if (index === -1) {
				state.userAssessment.user.push(item);
			} else {
				state.userAssessment.user[index] = item;
			}
		},
		setUserAssessment(state, action) {
			state.userAssessment = action.payload;
		},
		toggleConsentAdditional1(state) {
			state.consentAdditional1 = !state.consentAdditional1;
		},
		toggleConsentAdditional2(state) {
			state.consentAdditional2 = !state.consentAdditional2;
		}
	},
	extraReducers: (builder) => {
		builder.addCase(initStoreState, (state, action) => {
			const check = action.payload;
			const persona = check.persona;

			state.firstName = persona.firstName;
			state.lastName = persona.lastName;
			state.country = persona.residence.country || '';
			state.dateOfBirth = persona.dateOfBirth || '';
			state.email = persona.contacts.email || '';
			state.phoneNumber = persona.contacts.phoneNumber || '';
		});
	}
});

export const personaActions = personaSlice.actions;

export const { setPersonaInfo, setPhoneNumber, updatePersonaInformation, resetPersonaInformation } = personaActions;

// selectors
export const selectPersona = (state: RootState) => state.persona;
export const selectPersonaEmail = (state: RootState) => state.persona.email;
export const selectPersonaPhoneNumber = (state: RootState): string | null => state.persona.phoneNumber;
export const selectConsentCollectingData = (state: RootState) => state.persona.consentCollectingData;
export const selectConsetForwardingData = (state: RootState) => state.persona.consentForwardingData;
export const selectConsentAdditional1Data = (state: RootState) => state.persona.consentAdditional1 || false;
export const selectConsentAdditional2Data = (state: RootState) => state.persona.consentAdditional2 || false;

export const selectPersonaInformation = createSelector(
	(state: RootState): PersonaInformation => {
		return {
			firstName: state.persona.firstName,
			lastName: state.persona.lastName,
			dateOfBirth: state.persona.dateOfBirth,
			country: state.persona.country
		};
	},
	(persona) => {
		return persona;
	}
);

export default personaSlice;
